import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Stack, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { v4 as uuidv4 } from 'uuid';

import { PhoneBase, InputUI, ButtonUI, CheckboxUI } from '../Interface';
import itemService from '../../service/item.service';
import { DocumentType } from '../../config/DocumentType';
import dayjs from 'dayjs';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useUsers from '../../context/UsersContext';
import { cleanValues } from '../../utils/helper';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import projectMaterialService from '../../service/projectMaterial.service';
import SearchDialog from './SearchDialog';
import ItemsDialog from './ItemsDialog';

const MODE_CONFIG = {
  issue: {
    phoneName: 'ProjectIssue',
    title: 'Kiadás projektre',
    successMessage: 'A projekt kiadás sikeresen rögzítve lett.',
    docTypeId: DocumentType.ProjectIssue,
  },
  receipt: {
    phoneName: 'ProjectReceipt',
    title: 'Visszavét projektről',
    successMessage: 'A projekt visszavét sikeresen rögzítve lett.',
    docTypeId: DocumentType.ProjectReturn,
  },
};

const ProjectMaterialForm = ({ mode }) => {
  const itemInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { getParam, params } = useParam();
  const [openSearch, setOpenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [itemFilter, setItemFilter] = useState('');
  const [docLines, setDocLines] = useState([]);
  const [note, setNote] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openItemsDialog, setOpenItemsDialog] = useState(false);
  const [isSearchingItems, setIsSearchingItems] = useState(false);

  const isReceipt = mode === 'receipt';
  const modeConfig = MODE_CONFIG[mode] || MODE_CONFIG.issue;
  const selectedProjectId = selectedProject?.projectId ?? selectedProject?.id ?? null;

  useEffect(() => {
    Object.keys(config).length === 0 && getConfig();
    Object.keys(params).length === 0 && getParam();
    Object.keys(user).length === 0 && getUser();
    setOpenSearch(true);
  }, []);

  useEffect(() => {
    setDocLines([]);
  }, [selectedProject]);

  useEffect(() => {
    setItemFilter('');
    setItems([]);
    setOpenItemsDialog(false);
  }, [selectedProjectId]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const defaultDocumentLine = {
    lineStatus: 'C',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    isBillable: false,
    oQty: 0,
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    warranty: false,
    docLineBatchSn: [],
  };

  const defaultValues = {
    docTypeId: modeConfig.docTypeId,
    docStatus: 'C',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: config?.defaultWarehouse || null,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const totalQtyByItemCode = (itemCode) => {
    return docLines.filter((line) => line.itemCode === itemCode).reduce((sum, line) => sum + Number(line.qty || 0), 0);
  };

  const handleQtyChange = (id) => (e) => {
    const val = Math.max(0, Number(e.target.value));

    if (isReceipt) {
      const line = docLines.find((currentLine) => currentLine.id === id);
      if (!line) return;

      const total = totalQtyByItemCode(line.itemCode) - Number(line.qty || 0) + val;

      if (total > Number(line.oQty || 0)) {
        addNotification({
          content: 'A cikk mennyisége meghaladja a projektre kiadott mennyiséget!',
          type: NotificationType.ERROR,
        });
        return;
      }
    }

    setDocLines((prev) => prev.map((line) => (line.id === id ? { ...line, qty: val } : line)));
  };

  const handleBillableChange = (id) => (e) => {
    const checked = e.target.checked;
    setDocLines((prev) => prev.map((line) => (line.id === id ? { ...line, isBillable: checked } : line)));
  };

  const handleWarrantyChange = (id) => (e) => {
    const checked = e.target.checked;
    setDocLines((prev) => prev.map((line) => (line.id === id ? { ...line, warranty: checked } : line)));
  };

  const handleOpenSearch = () => {
    setOpenSearch(true);
    setSearchQuery('');
  };

  const handleCloseSearch = () => {
    setOpenSearch(false);
    setTimeout(() => {
      if (itemInputRef.current) itemInputRef.current.focus();
    }, 0);
  };

  const handleOpenItemsDialog = () => {
    setOpenItemsDialog(true);
  };

  const addItem = (data) => {
    const newLine = {
      ...defaultDocumentLine,
      id: uuidv4(),
      itemCode: data.itemCode,
      itemName: data.itemName,
      qty: 1,
      whsCode: config.defaultWarehouse || null,
      quantityUnitId: data.quantityUnit?.id,
      quantityUnitCode: data.quantityUnit?.code,
      quantityUnitName: data.quantityUnit?.name,
      projectId: selectedProjectId,
      oQty: Number(data.oQty || 0),
    };

    setDocLines((prev) => [...prev, newLine]);
    setItemFilter('');
  };

  const checkReceiptItem = (itemCode) => {
    projectMaterialService.checkReceiptItem({ itemCode, projectId: selectedProjectId }).then((data) => {
      if (!data?.itemCode) return;

      const existingQty = totalQtyByItemCode(data.itemCode);
      if (existingQty >= Number(data.oQty || 0)) {
        addNotification({
          content: 'A cikk mennyisége meghaladja a projektre kiadott mennyiséget!',
          type: NotificationType.ERROR,
        });
        return;
      }

      addItem(data);
    });
  };

  useEffect(() => {
    if (!selectedItem) return;

    if (isReceipt) {
      checkReceiptItem(selectedItem.itemCode);
    } else {
      addItem(selectedItem);
    }

    setSelectedItem(null);
    setItemFilter('');
    setItems([]);
    setOpenItemsDialog(false);
  }, [selectedItem]);

  const searchItem = (query) => {
    const val = String(query ?? itemFilter).trim();
    if (!selectedProjectId || !val) {
      setItems([]);
      setIsSearchingItems(false);
      return;
    }

    setIsSearchingItems(true);
    itemService
      .findItem({ query: val, fullSearch: true })
      .then((data) => {
        setItems(data || []);
      })
      .finally(() => {
        setIsSearchingItems(false);
      });
  };

  useEffect(() => {
    const val = String(itemFilter || '').trim();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!selectedProjectId || !val) {
      setItems([]);
      setIsSearchingItems(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (!openItemsDialog) {
        setOpenItemsDialog(true);
      }
      searchItem(val);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [itemFilter, selectedProjectId]);

  const handleRemoveLine = (id) => {
    setDocLines((prev) => prev.filter((line) => line.id !== id));
  };

  const handleRegister = () => {
    const errors = [];

    if (!selectedProjectId) errors.push('Nincs kiválasztott projekt.');
    if (docLines.length === 0) errors.push('Nincs hozzáadott cikk.');
    if (docLines.some((line) => Number(line.qty) <= 0))
      errors.push('Minden mennyiségnek 0-nál nagyobbnak kell lennie.');

    if (errors.length) {
      addNotification({
        content: errors.join('\n'),
        type: NotificationType.ERROR,
      });
      return;
    }

    const doc = {
      ...cleanValues(defaultValues),
      projectId: selectedProjectId,
      note,
      docLine: docLines.map((line, index) =>
        cleanValues({ ...line, projectId: selectedProjectId, visOrder: index + 1 })
      ),
      createdBy: user.userId,
    };

    projectMaterialService.createDocument(doc).then(() => {
      addNotification({
        content: modeConfig.successMessage,
        type: NotificationType.SUCCESS,
      });
      setSelectedProject(null);
      setDocLines([]);
      setNote('');
      setSearchQuery('');
      setOpenSearch(true);
    });
  };

  const isValid = !!selectedProjectId && docLines.length > 0 && docLines.every((line) => Number(line.qty) > 0);

  return (
    <PhoneBase name={modeConfig.phoneName} sx={{ p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {modeConfig.title}
      </Typography>
      <Box>
        <Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 80,
              mt: 1.5,
              p: 1.5,
              bgcolor: 'grey.100',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h7" component="h4">
                Projekt
              </Typography>
              <IconButton size="small" onClick={handleOpenSearch} aria-label="Keresés">
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>

            {selectedProject && (
              <Box
                key={selectedProjectId}
                sx={{
                  px: 1.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: 'white',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {selectedProject.projectName}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {getProjectOrderNumberName(params)}:
                  </Box>{' '}
                  {selectedProject.orderNumber}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 80,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h7" component="h4">
            Cikkek
          </Typography>

          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, bgcolor: 'white' }}>
              <InputUI
                autoFocus
                fullWidth
                size="small"
                placeholder="Cikknév/cikkszám"
                value={itemFilter}
                onChange={(e) => {
                  setItemFilter(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = String(itemFilter || '').trim();
                    if (!selectedProjectId) {
                      addNotification({
                        content: 'Előbb válasszon ki egy projektet!',
                        type: NotificationType.ERROR,
                      });
                      return;
                    }

                    if (!val) return;

                    if (searchTimeoutRef.current) {
                      clearTimeout(searchTimeoutRef.current);
                    }

                    handleOpenItemsDialog();
                    searchItem(val);
                  }
                }}
                inputRef={itemInputRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label="Keresés"
                        onClick={() => {
                          const val = String(itemFilter || '').trim();
                          if (!selectedProjectId) {
                            addNotification({
                              content: 'Előbb válasszon ki egy projektet!',
                              type: NotificationType.ERROR,
                            });
                            return;
                          }

                          if (!val) return;

                          if (searchTimeoutRef.current) {
                            clearTimeout(searchTimeoutRef.current);
                          }

                          handleOpenItemsDialog();
                          searchItem(val);
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Stack spacing={0.25} sx={{ mt: 1.5 }}>
            {docLines.map((item) => {
              const key = item.id;

              return (
                <Box
                  key={key}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'white',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {item.itemCode} - {item.itemName}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InputUI
                        name="qty"
                        type="number"
                        size="small"
                        sx={{ width: 110 }}
                        inputProps={{ min: 0, step: 1 }}
                        value={item.qty ?? 0}
                        onChange={handleQtyChange(key)}
                      />
                      <IconButton color="error" size="small" aria-label="Törlés" onClick={() => handleRemoveLine(key)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Box>
                      <CheckboxUI label="Számlázandó" checked={item.isBillable} onChange={handleBillableChange(key)} />
                      <CheckboxUI label="Garanciális" checked={item.warranty} onChange={handleWarrantyChange(key)} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 80,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h7" component="h4">
            Megjegyzés
          </Typography>

          <Box sx={{ mt: 1, bgcolor: 'white' }}>
            <InputUI
              fullWidth
              multiline
              minRows={3}
              placeholder="Megjegyzés"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Box>
        </Box>

        <Box textAlign="center" mt="2rem">
          <ButtonUI text="Rögzítés" className="bg-success" onClick={handleRegister} disabled={!isValid} />
        </Box>
      </Box>

      <SearchDialog
        open={openSearch}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        handleClose={handleCloseSearch}
        setSelectedProject={setSelectedProject}
      />

      <ItemsDialog
        open={openItemsDialog}
        handleClose={() => {
          setOpenItemsDialog(false);
          if (itemInputRef.current) itemInputRef.current.focus();
        }}
        items={items}
        itemFilter={itemFilter}
        setItemFilter={setItemFilter}
        isSearching={isSearchingItems}
        addItem={(item) => {
          setSelectedItem(item);
        }}
      />
    </PhoneBase>
  );
};

export default ProjectMaterialForm;
